import { SidebarTrigger } from "@/components/ui/sidebar"
import { Badge } from '@/components/ui/badge';
import { Sparkles, Moon, Sun, AlertTriangle, Clock } from 'lucide-react';
import { User } from 'firebase/auth';
import { useTheme } from '@/components/theme/ThemeProvider';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

type SubscriptionExpiry = Timestamp | Date | number | string;

interface WorkspaceHeaderProps {
  pathname: string;
  user: User | null;
  subscription?: {
    status: string;
    plan: string;
    expiry?: SubscriptionExpiry;
  } | null;
  effectivePlan?: string;
}

import { getPlanDisplayName, isTrialPlan } from '@/utils/planHelpers';

const toDateSafe = (v?: SubscriptionExpiry): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    const parsed = new Date(v);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof v.toDate === 'function') return v.toDate();
  return null;
};

const getTrialDaysRemaining = (expiry?: SubscriptionExpiry): number | null => {
  const expiryDate = toDateSafe(expiry);
  if (!expiryDate) return null;
  const diff = expiryDate.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const WorkspaceHeader = ({ pathname, user, subscription, effectivePlan }: WorkspaceHeaderProps) => {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

  const displayPlan = effectivePlan || subscription?.plan || '';
  const expiry = toDateSafe(subscription?.expiry);
  const isPaidPlan = !!displayPlan && !isTrialPlan(displayPlan);
  const isPaid = subscription?.status === 'active' || isPaidPlan;
  const isTrial = !isPaid && subscription?.status === 'trial' && !!expiry && expiry > new Date();
  const daysLeft = expiry ? getTrialDaysRemaining(subscription?.expiry) : null;

  const urgency = daysLeft === null ? null
    : daysLeft < 0 ? 'expired'
    : daysLeft <= 2 ? 'critical'
    : daysLeft <= 5 ? 'warning'
    : 'normal';

  const planLabel = isTrial
    ? 'Free Trial'
    : isPaidPlan
    ? getPlanDisplayName(displayPlan)
    : '';

  return (
    <div className="sticky top-0 z-30 flex flex-col">
      {/* Countdown Banner — trial always shows; paid plans only when ≤7 days remain */}
      {daysLeft !== null && expiry && (isTrial || (isPaid && daysLeft <= 7 && daysLeft > 0)) && (
        <div className={`w-full flex items-center justify-between px-6 py-2 text-[10px] font-black uppercase tracking-widest border-b transition-all
          ${isTrial
            ? (urgency === 'expired' || urgency === 'critical'
              ? 'bg-red-50 dark:bg-red-950/80 border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-300'
              : urgency === 'warning'
              ? 'bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-300'
              : 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-500/10 text-blue-700 dark:text-blue-300')
            : (urgency === 'critical'
              ? 'bg-red-50 dark:bg-red-950/80 border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-300'
              : 'bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-300')
          }`}>
          <div className="flex items-center gap-2">
            {(urgency === 'expired' || urgency === 'critical')
              ? <AlertTriangle size={12} className="text-red-500 dark:text-red-400 shrink-0" />
              : <Clock size={12} className="shrink-0" />
            }
            <span>
              {isTrial
                ? (urgency === 'expired'
                  ? 'Your trial has expired — upgrade to keep access'
                  : daysLeft === 0
                  ? 'Your trial expires today — upgrade to keep access'
                  : `Free trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining`)
                : (daysLeft <= 2
                  ? `${planLabel} expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'} — renew to keep access`
                  : daysLeft <= 5
                  ? `${planLabel} expires in ${daysLeft} days — renew to keep access`
                  : `${planLabel} — ${daysLeft} days remaining`)
              }
            </span>
          </div>
          <button
            onClick={() => navigate('/dashboard/billing')}
            className={`px-4 py-1 border text-[8px] font-black uppercase tracking-widest transition-all hover:opacity-80
              ${isTrial
                ? (urgency === 'critical'
                  ? 'border-red-300 dark:border-red-400/40 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20'
                  : urgency === 'warning'
                  ? 'border-amber-300 dark:border-amber-400/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20'
                  : 'border-blue-300 dark:border-blue-400/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20')
                : (urgency === 'critical'
                  ? 'border-red-300 dark:border-red-400/40 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20'
                  : 'border-amber-300 dark:border-amber-400/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20')
              }`}
          >
            {isTrial ? 'Upgrade Now →' : 'Renew →'}
          </button>
        </div>
      )}

      {/* Main Header */}
      <header className="h-12 sm:h-16 border-b border-gray-100 dark:border-white/5 bg-white/80 dark:bg-[#020617]/80 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-4">
          <SidebarTrigger className="hover:bg-gray-50 dark:hover:bg-white/5 text-black dark:text-white transition-colors" />
          <div className="flex items-center gap-2">
            <span className="hidden md:inline text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">Workspace //</span>
            <span className="font-medium text-lg tracking-tighter capitalize text-black dark:text-white">
              {pathname.split('/').pop() || 'Overview'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            className="rounded-none text-gray-400 hover:text-black dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Badge variant="outline" className="hidden sm:flex border-gray-200 bg-white text-black dark:bg-[#020617] dark:border-white/10 dark:text-white gap-2 px-3 py-1 rounded-none text-[9px] font-black uppercase tracking-widest">
            <Sparkles size={12} className="text-black dark:text-white" />
            {isTrial ? 'ON TRIAL' : displayPlan ? getPlanDisplayName(displayPlan) : 'INDIVIDUAL'}
          </Badge>
          <div
            onClick={() => navigate('/dashboard/account')}
            title="My Account"
            className="h-8 w-8 bg-black dark:bg-white ring-1 ring-gray-100 dark:ring-white/10 shadow-sm flex items-center justify-center text-white dark:text-black font-bold text-xs italic cursor-pointer hover:opacity-80 transition-opacity"
          >
            {user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || 'T'}
          </div>
        </div>
      </header>
    </div>
  );
};

export default WorkspaceHeader;
